import { useEffect, useRef } from "react"
import { useQueryState } from "nuqs"
import { DateTime } from "luxon"
import {
  useVideoScanFilterOptions,
  useVideoErrorSummary,
  useVideoStatsCases,
} from "../../hooks/useVideoLogs"
import { sourceOptions } from "../../constants"
import ApiError from "../../components/ApiError"
import { getDateRange } from "../../utils/datetime"
import NotFoundPhysicianList from "./NotFoundPhysicianList"
import { DoctorMatchPanel, QrDeliveryPanel } from "./SummaryPanels"
import VideoLogsFilterBar from "./VideoLogsFilterBar"

const PAGE_SIZE = 20

const CUSTOM = "Custom"
const DEFAULT_RANGE = "This Week"
const DATE_RANGE_OPTIONS = [
  "Today",
  "Yesterday",
  "This Week",
  "Last Week",
  "This Month",
  "Last Month",
  "All",
]
const DATE_RANGE_PRESETS = DATE_RANGE_OPTIONS.map((value) => ({
  value,
  label: value,
}))

const formatRangeLabel = (startIso, endIso) => {
  if (!startIso || !endIso) return ""
  const start = DateTime.fromISO(startIso)
  const end = DateTime.fromISO(endIso)
  if (!start.isValid || !end.isValid) return ""

  const sameMonth = start.hasSame(end, "month") && start.hasSame(end, "year")
  const startLabel = start.toFormat("MMM d")
  const endLabel = sameMonth ? end.toFormat("d") : end.toFormat("MMM d")
  return `${startLabel} – ${endLabel}`
}

const EducationalVideoLogs = () => {
  const [dateRange, setDateRange] = useQueryState("dateRange", {
    defaultValue: DEFAULT_RANGE,
  })
  const [dateFrom, setDateFrom] = useQueryState("dateFrom", {
    defaultValue: "",
  })
  const [dateTo, setDateTo] = useQueryState("dateTo", { defaultValue: "" })
  const [status, setStatus] = useQueryState("status", { defaultValue: "All" })
  const [patientIssuer, setPatientIssuer] = useQueryState("patientIssuer", {
    defaultValue: "All",
  })
  const [source, setSource] = useQueryState("source", { defaultValue: "All" })

  const sentinelRef = useRef(null)

  const isCustomDateRange = dateRange === CUSTOM

  const getDateBounds = () => {
    if (isCustomDateRange) {
      return { start: dateFrom || "", end: dateTo || "" }
    }
    if (dateRange === "All") {
      return { start: "", end: "" }
    }
    // A hand-edited URL can carry a range name that getDateRange would throw on.
    const range = DATE_RANGE_OPTIONS.includes(dateRange)
      ? dateRange
      : DEFAULT_RANGE
    const interval = getDateRange(range)
    return { start: interval.start.toISODate(), end: interval.end.toISODate() }
  }

  const { start, end } = getDateBounds()
  const timePeriod = start || end ? [start, end].join(",") : undefined
  const rangeLabel = formatRangeLabel(start, end)

  const dateRangeTriggerLabel =
    isCustomDateRange && rangeLabel
      ? rangeLabel
      : isCustomDateRange
        ? CUSTOM
        : dateRange

  const handleSelectDateRangePreset = (range) => setDateRange(range)

  const handleApplyCustomDateRange = (from, to) => {
    setDateFrom(from)
    setDateTo(to)
    setDateRange(CUSTOM)
  }

  // Seeds the custom calendar from whatever range is already on screen, so it
  // opens on something meaningful instead of blank.
  const getDateRangeCustomSeed = () => {
    if (isCustomDateRange) return [dateFrom, dateTo]
    if (dateRange === "All") {
      const interval = getDateRange(DEFAULT_RANGE)
      return [interval.start.toISODate(), interval.end.toISODate()]
    }
    const named = DATE_RANGE_OPTIONS.includes(dateRange)
      ? dateRange
      : DEFAULT_RANGE
    const interval = getDateRange(named)
    return [interval.start.toISODate(), interval.end.toISODate()]
  }

  const { data: filterOptions } = useVideoScanFilterOptions()
  const issuerOptions = ["All", ...(filterOptions?.patientIssuers || [])]

  const {
    data: errorSummary = {
      failedDistinctAccessions: 0,
      successDistinctAccessions: 0,
    },
  } = useVideoErrorSummary({
    timePeriod,
    patientIssuer: patientIssuer === "All" ? undefined : patientIssuer,
    source: source === "All" ? undefined : source,
  })

  const {
    data: { summary, categories, groups, total } = {
      summary: {
        totalRecords: 0,
        distinctAccessions: 0,
        genericAttached: 0,
        uniqueDoctors: 0,
      },
      categories: [],
      groups: [],
      total: 0,
    },
    isLoading: isPhysiciansLoading,
    isError,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useVideoStatsCases({
    limit: PAGE_SIZE,
    filters: {
      timePeriod,
      patientIssuer: patientIssuer === "All" ? undefined : patientIssuer,
      source: source === "All" ? undefined : source,
      category: "NOT_FOUND",
    },
  })

  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage()
        }
      },
      { rootMargin: "200px", threshold: 0.1 }
    )

    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  const handleClearFilters = () => {
    setDateRange(DEFAULT_RANGE)
    setDateFrom("")
    setDateTo("")
    setStatus("All")
    setPatientIssuer("All")
    setSource("All")
  }

  if (isError) {
    return (
      <div className="mt-4">
        <ApiError axiosError={error} />
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-3 pb-4">
      <h1 className="shrink-0 text-2xl font-bold">
        Educational Video QR Code Analytics
      </h1>

      <div className="shrink-0">
        <VideoLogsFilterBar
          dateRangeCustomLabel={CUSTOM}
          dateRangePresets={DATE_RANGE_PRESETS}
          dateRangeTriggerLabel={dateRangeTriggerLabel}
          dateRangeValue={dateRange}
          issuerOptions={issuerOptions}
          patientIssuer={patientIssuer}
          source={source}
          sourceOptions={["All", ...sourceOptions]}
          status={status}
          onApplyCustomDateRange={handleApplyCustomDateRange}
          onClearFilters={handleClearFilters}
          onGetDateRangeCustomSeed={getDateRangeCustomSeed}
          onPatientIssuer={setPatientIssuer}
          onSelectDateRangePreset={handleSelectDateRangePreset}
          onSource={setSource}
          onStatus={setStatus}
        />
      </div>

      <div className="shrink-0 grid gap-3 lg:grid-cols-2">
        {/* Distinct accessions, not raw log rows — a re-sent report logs the
            same accession twice. */}
        <QrDeliveryPanel
          attached={errorSummary.successDistinctAccessions}
          failed={errorSummary.failedDistinctAccessions}
          generic={summary.genericAttached}
          total={summary.distinctAccessions}
        />
        <DoctorMatchPanel
          categories={categories}
          uniqueDoctors={summary.uniqueDoctors}
        />
      </div>

      <NotFoundPhysicianList
        groups={groups}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        isLoading={isPhysiciansLoading}
        sentinelRef={sentinelRef}
        total={total}
        onLoadMore={fetchNextPage}
      />
    </div>
  )
}

export default EducationalVideoLogs
